import React, { FC, JSX, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { LocationDescriptorObject } from 'history';

import {
	EVINCED_SEVERITIES,
	EvLinkTypes,
	EvSpinner,
	FormatHelper,
	HandsIcon,
	useSkipLinks
} from '@evinced-private/ui-common';

import { ComponentsCharts } from 'src/components/charts/ComponentsChartsV3';
import { EvincedAdvantageChartV3 } from 'src/components/charts/EvincedAdvantage';
import { IssuesCharts } from 'src/components/charts/IssuesChartsV3';
import { PagesCharts } from 'src/components/charts/PagesChartsV3';
import { ChartsSectionV3 } from 'src/components/data-displays/overview-charts/charts-section/ChartsSectionV3';
import { ApiErrorHelper } from 'src/helpers/ApiErrorHelper';
import { useGetProperty } from 'src/hooks/queries/useGetProperty';
import { useScanOverview } from 'src/hooks/queries/useGetScanOverview';
import { useGetScansScore } from 'src/hooks/queries/useGetScansScore';
import { IServerError } from 'src/interfaces/ServerError';
import { SummaryItem } from 'src/interfaces/SummaryItem';
import { useConfiguration } from 'src/providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from 'src/services/LocalTogglesService';

import { EvLinkLocal } from '../../../../components/common/ev-link-local/EvLinkLocal';
import { SummaryPane } from '../../../../components/common/summary-pane/SummaryPane';
import { SummaryPaneV2 } from '../../../../components/common/summary-pane/SummaryPaneV2';
import { GeneralError } from '../../../../components/general-error/GeneralError';
import { RoutesHelper } from '../../../../helpers/RoutesHelper';
import { ScanAndPropertyResultsHelper } from '../../../../helpers/ScanAndPropertyResultsHelper';
import { roundScore } from '../../../../helpers/ScoreHelper';
import {
	buildDeduplicatedSeveritySummary,
	resolveSeverityLabel
} from '../../../../helpers/SeverityLabelHelper';
import { TabsViews } from '../../../../types/TabsViews';
import { LoadErrorMsgLayout } from '../../../property/LoadErrorMsgLayout';

import { TopPagesByIssueCountTable } from './tables/top-pages/TopPagesByIssueCountTableV2';

import './ScanOverviewView.scss';

interface IScanOverviewViewProps {
	scanId: string;
	propertyId: string;
	viewId?: string;
	renderActionButtons?: () => JSX.Element;
}

const ScanOverviewViewV3: FC<IScanOverviewViewProps> = ({
	scanId,
	propertyId,
	viewId,
	renderActionButtons
}: IScanOverviewViewProps) => {
	const history = useHistory();
	const { data: scanOverview, isLoading, error } = useScanOverview(scanId, viewId);
	const {
		data: scanScoreData,
		isLoading: isScanScoreLoading,
		error: scanScoreError
	} = useGetScansScore([scanId], viewId);
	const { data: property, isLoading: isPropertyLoading } = useGetProperty(propertyId);

	const scanScore = roundScore(scanScoreData?.[0]?.scoreResult?.score) || 0;
	const pagesOverview = scanOverview?.pages;
	const issuesOverview = scanOverview?.issues;
	const componentsOverview = scanOverview?.components;
	const customSeverityNames = useMemo(
		() => property?.scanConfiguration?.customSeverityNames ?? {},
		[property]
	);

	const { getToggle } = useConfiguration();
	const enableEvincedAdvantageDiagram = getToggle(
		SiteScannerUiToggles.ENABLE_EVINCED_ADVANTAGE_DIAGRAM
	);

	const totalCriticalIssues = useMemo(
		() =>
			ScanAndPropertyResultsHelper.getIssuesCount(
				issuesOverview,
				EVINCED_SEVERITIES.CRITICAL,
				customSeverityNames
			),
		[issuesOverview, customSeverityNames]
	);

	const criticalLabel = useMemo(
		() => resolveSeverityLabel(EVINCED_SEVERITIES.CRITICAL, customSeverityNames),
		[customSeverityNames]
	);

	// Apply general dedup: when multiple defaults map to the same custom name (e.g. CRITICAL+SERIOUS → "High")
	// only one summary line is rendered. The first default in the list is what we want to surface in the
	// scan overview pane (Critical or its custom equivalent).
	const criticalSummary = useMemo(
		() =>
			buildDeduplicatedSeveritySummary(
				issuesOverview?.severities,
				[EVINCED_SEVERITIES.CRITICAL],
				customSeverityNames
			)[0],
		[issuesOverview, customSeverityNames]
	);

	const noIssues = useMemo(
		() => pagesOverview?.overview?.pagesWithIssues === 0 && issuesOverview?.issuesCount === 0,
		[pagesOverview, issuesOverview]
	);

	// updating skip links data when page is loaded
	useSkipLinks(!isLoading);

	const renderLinkToAllComponents = useCallback((): JSX.Element => {
		const url = RoutesHelper.getScanResultComponentsBreakdownPath(propertyId, scanId);
		return (
			<EvLinkLocal
				url={url}
				linkText="View all components"
				ariaLabel="view all components"
				type={EvLinkTypes.DARK}
			/>
		);
	}, [propertyId, scanId]);

	if (error || scanScoreError) {
		return (
			<div className="scan-overview-view">
				<GeneralError title="Error loading scan">{`Error occured while getting scan data. ${ApiErrorHelper.getErrorMessage(error as unknown as IServerError) ?? ''}`}</GeneralError>
			</div>
		);
	}

	if (isLoading || isScanScoreLoading || isPropertyLoading) {
		return (
			<div className="scan-overview-view">
				<EvSpinner />
			</div>
		);
	}

	if (noIssues) {
		return (
			<LoadErrorMsgLayout
				mainTitle="No issues"
				secondaryMsg={[
					"This test ran successfully and didn't contain issues.",
					'Keep up the good work.'
				]}
				icon={HandsIcon}
			/>
		);
	}

	const getSummaryPaneDataV2 = (): SummaryItem[] => {
		return [
			{
				value: `${scanScore}`,
				label: 'Score / 100',
				isDashedBorder: true
			},
			{
				value: FormatHelper.formatNumber(issuesOverview.issuesCount),
				label: 'Number of issues'
			},
			{
				value: FormatHelper.formatNumber(criticalSummary?.value ?? totalCriticalIssues),
				label: `${criticalSummary?.label ?? criticalLabel} issues`
			},
			{
				value: FormatHelper.formatNumber(pagesOverview.overview.pagesWithIssues),
				label: 'Pages with issues'
			}
		];
	};

	const redirectToPage = (stateKey, stateValue, tabView: TabsViews): void => {
		history.push({
			pathname: RoutesHelper.getScanDetailsView(propertyId, scanId, tabView),
			state: {
				[stateKey]: stateValue,
				isTemporary: true
			}
		} as LocationDescriptorObject);
	};

	const redirectToPagesPage = (stateKey, stateValue): void => {
		redirectToPage(stateKey, stateValue, TabsViews.PAGES);
	};

	const redirectToAllIssuesPage = (stateKey, stateValue): void => {
		redirectToPage(stateKey, stateValue, TabsViews.BREAKDOWN);
	};

	const redirectToComponentsPage = (componentId): void => {
		history.push({
			pathname: RoutesHelper.getComponentDrilldownPath(propertyId, scanId, componentId),
			state: {
				isTemporary: true
			}
		} as LocationDescriptorObject);
	};

	const top5worstText = 'Top 5 worst performing pages';
	const groupingIntoComponentsText = 'Grouping of issues into components';

	return (
		<div className="scan-overview-view">
			<SummaryPane
				items={getSummaryPaneDataV2()}
				sectionLabel="Scan summary"
				className="overview-summary-pane"
				renderActionButtons={renderActionButtons}
			/>

			<IssuesCharts
				severitiesData={issuesOverview.severities}
				totalIssues={issuesOverview.issuesCount}
				onItemClick={redirectToAllIssuesPage}
			/>

			<SummaryPaneV2
				className="grouping-of-issues-into-components"
				title={groupingIntoComponentsText}
				items={[
					{
						label: 'Total number of components',
						value: FormatHelper.formatNumber(componentsOverview.componentsCount)
					},
					{
						label: `Components with ${criticalLabel.toLowerCase()} issues`,
						value: FormatHelper.formatNumber(componentsOverview.criticalComponentsCount)
					}
				]}
				sectionLabel={groupingIntoComponentsText}
				renderActionButtons={renderLinkToAllComponents}
			/>

			<ComponentsCharts
				scanId={scanId}
				propertyId={propertyId}
				componentsCountHash={componentsOverview}
				totalCriticalIssues={totalCriticalIssues}
				customSeverityNames={customSeverityNames}
				onComponentTableLinkClick={redirectToComponentsPage}
			/>

			<PagesCharts
				pagesSeverities={pagesOverview.severities}
				pagesScanned={pagesOverview.overview.scanned}
				onBarItemClick={redirectToPagesPage}
			/>

			<ChartsSectionV3
				sectionTextBlockTitle={top5worstText}
				sectionLabel={top5worstText}
				skiplinkId="top-5-worst-performing-pages"
			>
				<div
					className={`overview-tables-section ${enableEvincedAdvantageDiagram ? 'gap-10' : 'chart-wrapper'}`}
				>
					<TopPagesByIssueCountTable scanId={scanId} propertyId={propertyId} />
					{enableEvincedAdvantageDiagram ? (
						<EvincedAdvantageChartV3
							severitiesData={issuesOverview.severities}
							totalIssues={totalCriticalIssues}
							customSeverityNames={customSeverityNames}
						/>
					) : null}
				</div>
			</ChartsSectionV3>
		</div>
	);
};

export { ScanOverviewViewV3 };
